// Edit Role dialog — pre-filled name + description form.

import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'

const editRoleSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or fewer'),
  description: z.string().max(500, 'Description must be 500 characters or fewer').optional(),
})

type EditRoleFormData = z.infer<typeof editRoleSchema>

interface EditRoleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentName: string
  currentDescription: string | null
  onSubmit: (data: { name?: string; description?: string }) => Promise<void>
}

export function EditRoleDialog({
  open,
  onOpenChange,
  currentName,
  currentDescription,
  onSubmit,
}: EditRoleDialogProps) {
  const [submitting, setSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<EditRoleFormData>({
    resolver: zodResolver(editRoleSchema),
    defaultValues: { name: currentName, description: currentDescription ?? '' },
  })

  // Reset form when dialog opens with new values
  useEffect(() => {
    if (open) {
      reset({ name: currentName, description: currentDescription ?? '' })
    }
  }, [open, currentName, currentDescription, reset])

  async function handleFormSubmit(data: EditRoleFormData) {
    setSubmitting(true)
    try {
      await onSubmit({
        name: data.name !== currentName ? data.name : undefined,
        description: (data.description ?? '') !== (currentDescription ?? '')
          ? (data.description || '')
          : undefined,
      })
      onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Role</DialogTitle>
          <DialogDescription>Update the role name or description.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-role-name">Name</Label>
            <Input id="edit-role-name" {...register('name')} autoFocus />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-role-description">Description (optional)</Label>
            <Input id="edit-role-description" {...register('description')} />
            {errors.description && (
              <p className="text-sm text-destructive">{errors.description.message}</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !isDirty}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
